import { useEffect } from 'react';
import {
  ASSUMED_KEYBOARD_RATIO,
  ASSUME_KEYBOARD_AFTER_MS,
  KEYBOARD_MIN_PX,
  centreInScroller,
  focusedField,
  watchKeyboardGeometry,
  watchUserGestures,
} from './use-visual-viewport-sheet-height';
import { isAndroidWebView } from './in-app-browser';

/** The custom property a page's scroller adds to its bottom padding. */
export const KB_PAD_VAR = '--kb-pad';

/**
 * The page-level twin of the sheet hook's assumed keyboard.
 *
 * A bottom sheet can be lifted clear of a keyboard that resizes nothing; a
 * PAGE cannot — but it can be given somewhere to scroll to. Inside the
 * Instagram/Telegram in-app browsers on Android the keyboard opens without
 * shrinking either viewport, so the registration form (a 100vh box centred
 * on the screen, nothing to scroll) and the app shell's inner scroller
 * (`h-[100dvh]`, sized to a screen the keyboard now covers half of) both
 * leave the lower fields unreachable: «клавиатура перекрывает поля, скролл
 * не работает» (tester, Android 16, 2026-09-22).
 *
 * Same detection as the sheet hook — a field focused for
 * `ASSUME_KEYBOARD_AFTER_MS` on a touch screen, neither viewport moved, **inside
 * an Android WebView, after a real user press** — and one effect:
 * `--kb-pad` on the root, worth the assumed keyboard, which the
 * page's scroller adds to its bottom padding — so the covered part can be
 * scrolled up into the visible half — and the focused field is scrolled
 * into the middle of that half. A real resize clears it; so does the blur
 * that ends typing.
 *
 * `scrollerRef` — the element that scrolls (the shell's `<main>`); without it
 * the document scrolls, which is the standalone pages' case.
 *
 * ⚠️ THE TWO GATES ADDED 2026-09-26, and why a page-level pad needed them as
 * much as the sheet did. On the owner's iPhone (Chrome iOS 153 / WebKit,
 * 393×665, 11:46 UTC) a declined card made the card modal call `.focus()` on the
 * card-number field from its error handler. iOS opens NO keyboard for a
 * programmatic focus outside a user gesture and resizes nothing, so 450 ms later
 * both hooks fired on an idle page: the sheet lifted 299px and this one wrote
 * `--kb-pad: 299px` — 299px of padding under a form with no keyboard anywhere.
 * So the assumption now requires an Android WebView (every iOS engine shrinks
 * the visual viewport for a real keyboard, so "nothing shrank" is evidence of no
 * keyboard, not of an invisible one) AND a touch/pointer press within
 * `GESTURE_MAX_AGE_MS` before the focus. Both live in the sheet hook, shared
 * from there, so the two can never disagree about what a keyboard is.
 */
export function useKeyboardOverlayPad(scrollerRef?: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    const vv = window.visualViewport;
    let h0 = 0;
    let v0 = 0;
    let timer = 0;
    let blurTimer = 0;
    let settleTimer = 0;
    const SETTLE_MS = 300;
    let active = false;
    /** The one population this pad is for; see the docstring. Read once — a UA
     *  does not change under a mounted page. */
    const androidWebView = isAndroidWebView();
    const gestures = watchUserGestures();
    /*
     * What this engine does with a keyboard, learnt while the page is open —
     * the same watcher the two sheet hooks use, so «is there a keyboard» has one
     * answer across the app rather than three. Here it only ever RULES THE PAD
     * OUT: an engine that has been seen to shrink `innerHeight` for a keyboard
     * needs no padding guess, because the page it is padding got shorter and the
     * scroller already reaches everything.
     */
    const layout = watchKeyboardGeometry(vv);
    const coarse = () => window.matchMedia?.('(pointer: coarse)').matches ?? false;
    /** The shared predicate — the same three tags plus `contenteditable`. */
    const field = focusedField;
    const viewportShrank = () =>
      window.innerHeight < h0 - KEYBOARD_MIN_PX || (vv != null && vv.height < v0 - KEYBOARD_MIN_PX);
    const clear = () => {
      if (!active) return;
      active = false;
      root.style.removeProperty(KB_PAD_VAR);
    };
    const assume = () => {
      // ⚠️ Order is cheapest-first, but the two gates are not optimisations:
      // outside an Android WebView there is nothing to assume (iOS reports a real
      // keyboard by shrinking), and without a press the focus was code's, not the
      // user's — 299px of padding under an idle iPhone form, 2026-09-26.
      if (!androidWebView || !gestures.recent()) return;
      /*
       * ⚠️ …and not once this engine has PROVED it resizes the layout viewport for
       * the keyboard (2026-09-26). Some host apps' WebViews resize and some do
       * not, and the UA cannot tell them apart — only watching can. Where it does
       * resize, the page is already short and a 45% pad on top of that is pure
       * empty space. Measured on a paying customer's iPhone 15 Pro the same day:
       * an Instagram WKWebView took `innerHeight` 749 → 402 for the keyboard,
       * which is exactly this signal.
       */
      if (layout.resizesForKeyboard()) return;
      const el = field();
      if (!el || !coarse() || viewportShrank()) return;
      active = true;
      root.style.setProperty(
        KB_PAD_VAR,
        `${Math.round(window.innerHeight * ASSUMED_KEYBOARD_RATIO)}px`,
      );
      // Bring the field into the visible half — now, and again once the padding
      // has laid out: a rect read in the very next frame can still see the old
      // geometry (measured in Chromium, 2026-09-22), and nothing else fires here.
      const centre = () => {
        if (!active) return;
        const scroller = scrollerRef?.current;
        if (scroller) centreInScroller(el, scroller);
        else if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
      };
      requestAnimationFrame(centre);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(centre, SETTLE_MS);
    };
    const onFocusIn = () => {
      window.clearTimeout(blurTimer);
      layout.note();
      h0 = window.innerHeight;
      v0 = vv?.height ?? h0;
      window.clearTimeout(timer);
      timer = window.setTimeout(assume, ASSUME_KEYBOARD_AFTER_MS);
    };
    const onFocusOut = () => {
      window.clearTimeout(timer);
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        if (!field()) clear();
      }, 350);
    };
    const onResize = () => {
      // ⚠️ `note()` FIRST, and on every resize: this is the read that learns
      // «this engine shrinks `innerHeight` for the keyboard», which is what turns
      // the pad off for good on the WebViews that do.
      layout.note();
      if (active && viewportShrank()) clear();
    };
    /** The same handler plus the stamp that the visual viewport is still live. */
    const onVvResize = () => {
      layout.vvReported();
      onResize();
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', onResize);
    vv?.addEventListener('resize', onVvResize);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('resize', onResize);
      vv?.removeEventListener('resize', onVvResize);
      window.clearTimeout(timer);
      window.clearTimeout(blurTimer);
      window.clearTimeout(settleTimer);
      gestures.stop();
      clear();
    };
  }, [scrollerRef]);
}
